import { Control, Controller, FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import type { AISettings } from '@/UI/Identity/Identity.constants';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  /** When set, hovering the option shows this text as an "Example:" tooltip. */
  tooltip?: string;
}

interface ControlledSelectProps {
  control: Control<AISettings>;
  name: FieldPath<AISettings>;
  options: SelectOption[];
  /** field value is stored as a number (personality, creativity, reply, links) */
  numeric?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

export const ControlledSelect = ({
  control,
  name,
  options,
  numeric,
  disabled,
  placeholder,
  ...rest
}: ControlledSelectProps) => {
  const { t } = useTranslation('common');
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const currentValue = field.value != null ? String(field.value) : undefined;
        const current = options.find((o) => o.value === currentValue);
        return (
          <TooltipProvider>
            <Select
              value={currentValue}
              onValueChange={(val) => field.onChange(numeric ? Number(val) : val)}
              disabled={disabled}
            >
              <SelectTrigger className="w-full" data-testid={rest['data-testid']}>
                {/* Explicit children keep only the label in the trigger; the per-option
                    description stays in the dropdown list (matches the AI Model select). */}
                <SelectValue placeholder={placeholder}>{current?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                {options.map((opt) => {
                  const body = (
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{opt.label}</div>
                      {opt.description ? (
                        <div className="text-pretty text-xs font-normal text-muted-foreground">
                          {opt.description}
                        </div>
                      ) : null}
                    </div>
                  );
                  return (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={opt.description ? 'py-2.5' : undefined}
                    >
                      {opt.tooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{body}</TooltipTrigger>
                          <TooltipContent side="right">
                            <span className="block font-medium">{t('example')}:</span>
                            <span className="mt-1 block text-muted-foreground">{opt.tooltip}</span>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        body
                      )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </TooltipProvider>
        );
      }}
    />
  );
};
